import { useEffect, useRef } from "react";
import { axiosAuth } from "../api/axios";
import useAuth from "./useContexts";
import { setToken } from "../common/utils/tokenStorage";

export function useAxiosAuth() {
  const { auth, setAuth } = useAuth();

  // The interceptors read the token through a ref so a mid-session refresh is visible
  // to the very next request, rather than only after React re-renders this hook.
  const tokenRef = useRef<string>(auth?.token ?? "");

  useEffect(() => {
    tokenRef.current = auth?.token ?? "";
  }, [auth?.token]);

  useEffect(() => {
    const requestIntercept = axiosAuth.interceptors.request.use(
      (config: any) => {
        if (!config.headers["authorization"]) {
          config.headers["authorization"] = "Bearer " + tokenRef.current;
        }
        return config;
      },
      (error: any) => Promise.reject(error),
    );

    const responseIntercept = axiosAuth.interceptors.response.use(
      (response) => {
        // The backend slides the expiry window on active use and returns the
        // replacement here. Absent on requests that are far from expiry, and on the
        // endpoints it deliberately does not slide (see TokenRefreshable).
        const refreshed = response.headers?.["x-auth-token"];
        if (refreshed) {
          tokenRef.current = refreshed;
          setToken(refreshed);
          setAuth((prev: any) => ({ ...prev, token: refreshed }));
        }
        return response;
      },
      (error: any) => Promise.reject(error),
    );

    return () => {
      axiosAuth.interceptors.request.eject(requestIntercept);
      axiosAuth.interceptors.response.eject(responseIntercept);
    };
    // setAuth is a stable useState setter, so the interceptors register once per hook
    // instance instead of re-registering on every auth change. The ref above is what
    // makes that safe.
  }, [setAuth]);

  return axiosAuth as any;
}
